import { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Snackbar, Alert } from '@mui/material';
import api from '../../services/api';
import { useCall } from '../../contexts/CallContext';
import useCallLayout from '../../hooks/useCallLayout';

/**
 * JitsiCall — embeds Jitsi Meet via the IFrame API for voice/video consultations.
 *
 * Features:
 * - JWT auth: fetches short-lived Jitsi JWT before joining (Phase 0)
 * - Grace period: 60s reconnection window on network drop (PRD-006)
 * - Abuse protection: grace window shrinks to 15s after 3 drops (BILL-002)
 * - Tab suspension: detects mobile tab hide > 30s (PRD-005)
 * - Join signaling: POST /consultations/:id/joined on videoConferenceJoined
 */

const GRACE_NORMAL_MS = 60000;   // 60s grace for first 3 disconnections
const GRACE_SHORT_MS = 15000;    // 15s grace after 3+ disconnections
const GRACE_THRESHOLD = 3;       // After this many grace periods, shorten window
const TAB_SUSPEND_THRESHOLD = 30000; // 30s tab hidden = potential suspension

const QUALITY_POLL_INTERVAL_MS = 10000; // 10s between quality samples
const POOR_QUALITY_THRESHOLD = 30;      // Below 30% = poor quality

const JitsiCall = ({ consultationId, chatRoomId, displayName, videoMuted = false, onEnd, onJoined, callType }) => {
  const { setAudioMuted, setVideoMuted, setCallStartTime, setParticipantName, setJitsiApi, hasJitsiApi } = useCall();
  const { isMobile } = useCallLayout();
  const onEndRef = useRef(onEnd);
  onEndRef.current = onEnd;
  const onJoinedRef = useRef(onJoined);
  onJoinedRef.current = onJoined;
  const joinSignalled = useRef(false);
  const apiRef = useRef(null);
  const containerRef = useRef(null);
  const graceTimerRef = useRef(null);
  const graceCountRef = useRef(0);
  const tabHiddenAtRef = useRef(null);
  const intentionalHangupRef = useRef(false);
  const qualityStatsRef = useRef([]);
  const qualityIntervalRef = useRef(null);
  const callStartTimeRef = useRef(null);

  const [reconnecting, setReconnecting] = useState(false);
  const [tabWarning, setTabWarning] = useState(false);
  const [jwtError, setJwtError] = useState(null);

  const roomName = chatRoomId
    ? chatRoomId.replace(/[^a-zA-Z0-9]/g, '').toLowerCase()
    : `astrosastra${consultationId}`;

  // Get grace period duration based on how many times we've reconnected
  const getGraceDuration = useCallback(() => {
    return graceCountRef.current >= GRACE_THRESHOLD ? GRACE_SHORT_MS : GRACE_NORMAL_MS;
  }, []);

  // Start polling connection quality from the Jitsi API instance
  const startQualityPolling = useCallback((jitsiApi) => {
    callStartTimeRef.current = Date.now();
    qualityStatsRef.current = [];
    if (qualityIntervalRef.current) clearInterval(qualityIntervalRef.current);

    qualityIntervalRef.current = setInterval(() => {
      try {
        // getConnectionQuality returns a value for the local participant
        const stats = jitsiApi.getConnectionQuality && jitsiApi.getConnectionQuality();
        if (stats != null) {
          qualityStatsRef.current.push(stats);
        }
      } catch (e) {
        // Jitsi API may not support this method; ignore silently
      }
    }, QUALITY_POLL_INTERVAL_MS);
  }, []);

  // Stop polling and POST quality summary to backend (best-effort)
  const stopQualityPollingAndReport = useCallback(() => {
    if (qualityIntervalRef.current) {
      clearInterval(qualityIntervalRef.current);
      qualityIntervalRef.current = null;
    }

    const samples = qualityStatsRef.current;
    const durationSeconds = callStartTimeRef.current
      ? Math.round((Date.now() - callStartTimeRef.current) / 1000)
      : 0;

    if (samples.length === 0 && durationSeconds === 0) return;

    const avg = samples.length > 0
      ? samples.reduce((a, b) => a + b, 0) / samples.length
      : 0;
    const min = samples.length > 0 ? Math.min(...samples) : 0;
    const poorCount = samples.filter((q) => q < POOR_QUALITY_THRESHOLD).length;

    const summary = {
      avg_quality: Math.round(avg * 100) / 100,
      min_quality: Math.round(min * 100) / 100,
      total_samples: samples.length,
      poor_quality_count: poorCount,
      duration_seconds: durationSeconds,
    };

    // Best-effort — don't block call teardown on this
    api.post(`/consultations/${consultationId}/quality`, summary).catch(() => {});
  }, [consultationId]);

  // End the call — report grace count and quality to backend for audit
  const endCall = useCallback(() => {
    if (graceTimerRef.current) {
      clearTimeout(graceTimerRef.current);
      graceTimerRef.current = null;
    }
    setReconnecting(false);
    stopQualityPollingAndReport();
    // Report grace count to backend on end
    if (graceCountRef.current > 0) {
      api.post(`/consultations/${consultationId}/end`, {
        grace_period_count: graceCountRef.current,
      }).catch(() => {});
    }
    onEndRef.current && onEndRef.current();
  }, [consultationId, stopQualityPollingAndReport]);

  // Safety net: attempt to signal /end on page unload (tab close, navigation away)
  // Uses synchronous XHR as sendBeacon can't carry auth headers
  useEffect(() => {
    const handleUnload = () => {
      const token = localStorage.getItem('token');
      if (token && consultationId) {
        try {
          const baseUrl = process.env.REACT_APP_API_URL || '';
          const xhr = new XMLHttpRequest();
          xhr.open('POST', `${baseUrl}/api/v1/consultations/${consultationId}/end`, false); // sync
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          xhr.setRequestHeader('Content-Type', 'application/json');
          xhr.send(JSON.stringify({ grace_period_count: graceCountRef.current }));
        } catch (e) {
          // Best-effort — cleanup job (PRD-011) handles missed cases
        }
      }
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [consultationId]);

  // Client heartbeat — pings backend every 30s so the cleanup job knows we're alive.
  // If the tab crashes or browser is killed, heartbeat stops and cleanup force-ends billing.
  // This is a fallback safety net — primary dead-client detection is via Prosody XMPP presence.
  useEffect(() => {
    const heartbeatInterval = setInterval(() => {
      api.post(`/consultations/${consultationId}/heartbeat`).catch(() => {});
    }, 30000);
    // Send first heartbeat immediately
    api.post(`/consultations/${consultationId}/heartbeat`).catch(() => {});
    return () => clearInterval(heartbeatInterval);
  }, [consultationId]);

  // Toggle video when callType switches between 'call' and 'video' mid-session
  useEffect(() => {
    if (!apiRef.current || !callType) return;
    try {
      if (callType === 'call') {
        apiRef.current.executeCommand('toggleVideo'); // mute video
      } else if (callType === 'video') {
        apiRef.current.isVideoMuted().then((muted) => {
          if (muted) apiRef.current.executeCommand('toggleVideo'); // unmute video
        }).catch(() => {});
      }
    } catch (e) {
      console.warn('JitsiCall: failed to toggle video on type switch', e);
    }
  }, [callType]);

  // PRD-005: Tab suspension detection
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        tabHiddenAtRef.current = Date.now();
      } else {
        if (tabHiddenAtRef.current) {
          const hiddenDuration = Date.now() - tabHiddenAtRef.current;
          tabHiddenAtRef.current = null;
          if (hiddenDuration > TAB_SUSPEND_THRESHOLD) {
            console.log(`JitsiCall: tab was hidden for ${Math.round(hiddenDuration / 1000)}s`);
            setTabWarning(true);
            // Grace period (PRD-006) handles the actual reconnection if Jitsi disconnected
          }
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  useEffect(() => {
    const jitsiUrl = process.env.REACT_APP_JITSI_URL || '/jitsi';
    const isAbsolute = jitsiUrl.startsWith('http');

    const domain = isAbsolute
      ? new URL(jitsiUrl).host + new URL(jitsiUrl).pathname.replace(/\/$/, '')
      : `${window.location.host}${jitsiUrl}`;

    const useSSL = isAbsolute
      ? new URL(jitsiUrl).protocol === 'https:'
      : window.location.protocol === 'https:';

    const initJitsi = async () => {
      if (!containerRef.current || apiRef.current) return;
      // Defense-in-depth (#343): if another JitsiCall instance already
      // registered a Jitsi API in CallContext, abort to prevent duplicate
      // participants (same user joins room twice → audio breaks).
      if (hasJitsiApi()) {
        console.warn('JitsiCall: another Jitsi API already active in CallContext — skipping duplicate init');
        return;
      }
      if (!window.JitsiMeetExternalAPI) {
        console.error('JitsiMeetExternalAPI not available');
        return;
      }

      // Fetch Jitsi JWT token from backend — REQUIRED for Prosody auth.
      // Without JWT, user can't join XMPP, so Prosody can't track presence,
      // and server-side disconnect detection (webhooks) won't work.
      let jitsiToken = null;
      try {
        const tokenResp = await api.get(`/consultations/${consultationId}/jitsi-token`);
        jitsiToken = tokenResp.data.token;
        console.log('JitsiCall: JWT token obtained');
      } catch (err) {
        console.error('JitsiCall: JWT fetch failed — cannot start call:', err.message);
        setJwtError('Failed to authenticate for call. Please try again.');
        onEndRef.current && onEndRef.current();
        return;
      }

      try {
        console.log('JitsiCall: Initializing with domain:', domain, 'room:', roomName, 'ssl:', useSSL);
        const jitsiApi = new window.JitsiMeetExternalAPI(domain, {
          roomName,
          jwt: jitsiToken || undefined,
          parentNode: containerRef.current,
          width: '100%',
          height: '100%',
          ssl: useSSL,
          configOverwrite: {
            prejoinPageEnabled: false,
            enableLobbyChat: false,
            disableDeepLinking: true,
            requireDisplayName: false,
            enableNoisyMicDetection: false,
            startWithVideoMuted: videoMuted,
            startWithAudioMuted: false,
            p2p: { enabled: true },
            // Resource optimization to prevent tab crashes
            disableThirdPartyRequests: true,
            disableLocalVideoFlip: true,
            channelLastN: isMobile ? 1 : 2,
            resolution: isMobile ? 360 : 480,
            constraints: {
              video: { height: { ideal: isMobile ? 360 : 480, max: isMobile ? 480 : 720 } },
            },
            enableLayerSuspension: true,
            disableAudioLevels: true,
          },
          interfaceConfigOverwrite: {
            SHOW_JITSI_WATERMARK: false,
            SHOW_WATERMARK_FOR_GUESTS: false,
            SHOW_PROMOTIONAL_CLOSE_PAGE: false,
            TOOLBAR_ALWAYS_VISIBLE: false,
            INITIAL_TOOLBAR_TIMEOUT: 0,
            TOOLBAR_TIMEOUT: 0,
            TOOLBAR_BUTTONS: [],  // Hidden — custom CallToolbar replaces Jitsi's
            HIDE_INVITE_MORE_HEADER: true,
            DISABLE_JOIN_LEAVE_NOTIFICATIONS: true,
          },
          userInfo: {
            displayName: displayName || 'User',
          },
        });

        apiRef.current = jitsiApi;
        setJitsiApi(jitsiApi);

        // Sync mute state to CallContext for custom toolbar
        jitsiApi.addEventListener('audioMuteStatusChanged', ({ muted }) => setAudioMuted(muted));
        jitsiApi.addEventListener('videoMuteStatusChanged', ({ muted }) => setVideoMuted(muted));
        jitsiApi.isAudioMuted().then(setAudioMuted).catch(() => {});
        jitsiApi.isVideoMuted().then(setVideoMuted).catch(() => {});

        // PRD-012: Start polling call quality metrics
        startQualityPolling(jitsiApi);

        const signalJoin = () => {
          if (!joinSignalled.current) {
            joinSignalled.current = true;
            console.log('JitsiCall: signaling /joined to backend');
            api.post(`/consultations/${consultationId}/joined`).catch((err) => {
              console.error('Failed to signal join:', err);
            });
            onJoinedRef.current && onJoinedRef.current();
          }
        };

        // videoConferenceJoined — also handles reconnection after grace period
        jitsiApi.addEventListener('videoConferenceJoined', () => {
          console.log('JitsiCall: videoConferenceJoined (via API)');
          signalJoin();
          setCallStartTime(Date.now());
          // Fetch remote participant name (delayed for Jitsi to populate)
          setTimeout(() => {
            try {
              const participants = jitsiApi.getParticipantsInfo();
              const remote = participants?.find(p => !p.local);
              if (remote) setParticipantName(remote.displayName || '');
            } catch (e) { /* ignore */ }
          }, 2000);

          // PRD-006: If we were in grace period, reconnection succeeded
          if (graceTimerRef.current) {
            console.log('JitsiCall: reconnected during grace period');
            clearTimeout(graceTimerRef.current);
            graceTimerRef.current = null;
            setReconnecting(false);
          }
        });

        // readyToClose = intentional hangup (user clicked hang up button)
        jitsiApi.addEventListener('readyToClose', () => {
          console.log('JitsiCall: readyToClose (intentional hangup)');
          intentionalHangupRef.current = true;
          endCall();
        });

        // PRD-006: videoConferenceLeft — start grace period instead of ending immediately
        jitsiApi.addEventListener('videoConferenceLeft', () => {
          console.log('JitsiCall: videoConferenceLeft');

          // If intentional hangup, end immediately (no grace period)
          if (intentionalHangupRef.current) return;

          // Start grace period
          graceCountRef.current += 1;
          const graceDuration = getGraceDuration();
          console.log(
            `JitsiCall: starting grace period #${graceCountRef.current} (${graceDuration / 1000}s)`
          );
          setReconnecting(true);

          graceTimerRef.current = setTimeout(() => {
            console.log('JitsiCall: grace period expired — ending call');
            graceTimerRef.current = null;
            endCall();
          }, graceDuration);
        });

      } catch (err) {
        console.error('Failed to initialize JitsiMeetExternalAPI:', err);
      }
    };

    // If already loaded from a previous mount, initialize directly
    if (window.JitsiMeetExternalAPI) {
      console.log('JitsiCall: external_api.js already loaded');
      initJitsi();
      return () => {
        if (graceTimerRef.current) clearTimeout(graceTimerRef.current);
        if (qualityIntervalRef.current) clearInterval(qualityIntervalRef.current);
        if (apiRef.current) {
          apiRef.current.dispose();
          apiRef.current = null;
          setJitsiApi(null);
        }
      };
    }

    // Load external_api.js
    const scriptSrc = isAbsolute
      ? `${jitsiUrl}/external_api.js`
      : `${window.location.origin}/external_api.js`;

    const script = document.createElement('script');
    script.src = scriptSrc;
    script.async = true;

    script.onerror = () => {
      console.error('Failed to load Jitsi external_api.js');
    };

    script.onload = () => {
      console.log('JitsiCall: external_api.js loaded');
      initJitsi();
    };

    document.head.appendChild(script);

    return () => {
      if (graceTimerRef.current) clearTimeout(graceTimerRef.current);
      if (qualityIntervalRef.current) clearInterval(qualityIntervalRef.current);
      if (apiRef.current) {
        apiRef.current.dispose();
        apiRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consultationId, roomName]);

  return (
    <Box sx={{ width: '100%', height: '100%', minHeight: 400, position: 'relative' }}>
      {jwtError && (
        <Box
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            height: '100%', minHeight: 400, flexDirection: 'column', gap: 2,
          }}
        >
          <Alert severity="error">{jwtError}</Alert>
        </Box>
      )}
      <div
        ref={containerRef}
        style={{ width: '100%', height: '100%', minHeight: 400, display: jwtError ? 'none' : 'block' }}
      />

      {/* PRD-006: Reconnecting overlay */}
      {reconnecting && (
        <Box
          sx={{
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.7)',
            zIndex: 10,
            color: 'white',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Box sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
            Reconnecting...
          </Box>
          <Box sx={{ fontSize: '0.9rem', opacity: 0.8 }}>
            {graceCountRef.current >= GRACE_THRESHOLD
              ? 'Network unstable. Reconnecting quickly...'
              : 'Network interrupted. Waiting for reconnection...'}
          </Box>
        </Box>
      )}

      {/* PRD-005: Tab suspension warning */}
      <Snackbar
        open={tabWarning}
        autoHideDuration={6000}
        onClose={() => setTabWarning(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="warning" onClose={() => setTabWarning(false)}>
          Your call may have disconnected while the app was in background. Reconnecting...
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default JitsiCall;
